/**
 * Variant calling from a pairwise alignment vs a reference. Emits
 * SNVs + insertions + deletions in 1-based coordinates on the
 * reference. Suitable for variant-of-concern fingerprinting against
 * the catalog's Tier 2 reference genomes.
 */

import type { AlignmentResult } from './alignment.js';

export type VariantType = 'SNV' | 'insertion' | 'deletion';

export interface Variant {
  type: VariantType;
  /** 1-based position on the reference. For insertions, the position
   *  AFTER which the inserted bases sit. */
  position: number;
  ref: string;
  alt: string;
}

/**
 * Walk an aligned ref/query pair and emit variants.
 * Insertion = '-' in ref + non-gap in query → query has extra bases.
 * Deletion  = non-gap ref + '-' in query → query is missing bases.
 * SNV       = both non-gap + non-matching.
 */
export function callVariants(alignment: AlignmentResult, referenceFirst = true): Variant[] {
  const ref = referenceFirst ? alignment.alignedA : alignment.alignedB;
  const qry = referenceFirst ? alignment.alignedB : alignment.alignedA;
  if (ref.length !== qry.length) throw new Error('callVariants: aligned strings must be equal length');

  const variants: Variant[] = [];
  let refPos = 0; // 0-based; convert to 1-based for output.
  for (let i = 0; i < ref.length; i++) {
    const r = ref[i]!;
    const q = qry[i]!;
    if (r === '-' && q === '-') continue;
    if (r === '-') {
      // Insertion in query.
      let alt = q;
      let j = i + 1;
      while (j < ref.length && ref[j] === '-' && qry[j] !== '-') {
        alt += qry[j]!;
        j++;
      }
      variants.push({ type: 'insertion', position: refPos, ref: '-', alt });
      i = j - 1;
    } else if (q === '-') {
      let refBlock = r;
      let j = i + 1;
      while (j < ref.length && qry[j] === '-' && ref[j] !== '-') {
        refBlock += ref[j]!;
        j++;
      }
      variants.push({ type: 'deletion', position: refPos + 1, ref: refBlock, alt: '-' });
      refPos += refBlock.length;
      i = j - 1;
    } else {
      refPos++;
      if (r !== q) {
        variants.push({ type: 'SNV', position: refPos, ref: r, alt: q });
      }
    }
  }
  return variants;
}

/** Render a variant in HGVS-lite shorthand (e.g., 'A23403G', 'del140-145'). */
export function formatVariant(v: Variant): string {
  switch (v.type) {
    case 'SNV': return `${v.ref}${v.position}${v.alt}`;
    case 'insertion': return `ins${v.position}_${v.alt}`;
    case 'deletion': return `del${v.position}-${v.position + v.ref.length - 1}`;
  }
}
